import type { StartConfig } from "owa-shared-start";
import { updateServiceConfig } from "owa-service/lib/config";
import { lazyInitializeMetaOsAppSdkProxy, lazyGetOwaAuthTokenFromMetaOsHubProxy, lazyGetOwaDecodedAuthTokenFromMetaOsHubProxy, lazyInitializeMetaOsCapabilitiesProxy, lazyGetContextProxy, lazyPreInitOwaAccountSourceListStoreProxy, lazyPreInitAccountStoreForNestedAppAuthProxy, lazyGetAccountServiceConfigProxy, lazyIsNAAChannelRecommendedProxy, lazyGetResourceTokenFromMetaOsHubProxy, } from "owa-metaos-app-bootstrap/lib/lazyAppBoot";
import { setHostHub, setOpxHostData } from "owa-config";
import { isHostedCalendar } from "owa-config/lib/isHostedCalendar";
import { addBootCustomData, markFunction } from "owa-performance";
import { isMsalFlowEnabled } from "owa-msaljs/lib/isMsalFlowEnabled";

export const getRunBeforeStartForHosted = (config: StartConfig) => {
    const oldRunBeforeStart = config.runBeforeStart;
    
    return (startConfig: StartConfig): Promise<void | undefined> => markFunction(lazyInitializeMetaOsAppSdkProxy.import, "MetaOSAppBootImportScript")().then(async (initializeMetaOsAppSdk) => {
        // must finish before any other Hub SDK operations
        await initializeMetaOsAppSdk();
        
        const { app: { host: { name: metaOsHostName, clientType: metaOsHostClientType, sessionId: metaOsHostSessionId, ringId: metaOsHostRingId, }, locale: metaOsLocale, userClickTime, sessionId: metaOsAppSessionId, appLaunchId: metaOsAppLaunchId, }, user: metaOsUser, } = await lazyGetContextProxy.importAndExecute();
        
        // ensure this is set early before any calls that would depend on host info for eg. flighting
        setHostHub(metaOsHostName, metaOsHostClientType);
        
        // Stamp the same HostTelemetry payload that initializeMetaOSApp sets for the hosted
        // Calendar app, so CalendarDeepLink events (e.g. showFullComposePerf) can be joined
        // to the host's app launch telemetry via appLaunchId / appSessionId.
        if (config.app === "CalendarDeepLink" &&
            (metaOsHostClientType ||
                metaOsHostSessionId ||
                metaOsHostRingId ||
                metaOsAppSessionId)) {
            setOpxHostData(Promise.resolve({
                hostedScenario: "",
                hostTelemetry: JSON.stringify({
                    host: metaOsHostName,
                    hostType: metaOsHostClientType,
                    hostSessionId: metaOsHostSessionId,
                    hostRingId: metaOsHostRingId,
                    appSessionId: metaOsAppSessionId,
                    appLaunchId: metaOsAppLaunchId,
                }),
            }));
        }
        
        const getAuthTokenEnabled = !isMsalFlowEnabled() ||
            (!startConfig.msalConfiguration?.disableGetAuthTokenFallback &&
                !(await lazyIsNAAChannelRecommendedProxy.importAndExecute()));
        
        if (getAuthTokenEnabled) {
            // if using MSAL is disabled, or the host indicates it does not support NAA, override the
            // getAuthToken callback with the MetaOS Hub SDK getAuthToken callback
            await lazyGetOwaAuthTokenFromMetaOsHubProxy
                .import()
                .then(getAuthToken => updateServiceConfig({ getAuthToken }));
        }
        
        lazyInitializeMetaOsCapabilitiesProxy.importAndExecute();
        lazyIsNAAChannelRecommendedProxy.importAndExecute().then(isRecommended => {
            updateServiceConfig({ isNAAChannelRecommended: isRecommended });
        });
        
        // getResourceToken is backed by the MetaOS hub, which can mint tokens for any resource the
        // app is entitled to (e.g. IC3) regardless of whether OWA authenticates via the hub token or
        // requests back to the main thread here; the getAuthToken (app-origin) path stays gated on the
        // non-NAA flow below.
        updateServiceConfig({
            getResourceToken: lazyGetResourceTokenFromMetaOsHubProxy.importAndExecute,
        });
        
        if (metaOsHostSessionId) {
            addBootCustomData("HostSessionId", metaOsHostSessionId);
        }
        if (userClickTime) {
            addBootCustomData("HubLoadData", { userClickTime });
        }
        
        if (config.runAfterMetaOsAppInitialized) {
            config.runAfterMetaOsAppInitialized(metaOsHostName, metaOsHostClientType, metaOsUser?.id, metaOsUser?.tenant?.id, metaOsHostSessionId);
        }
        
        updateServiceConfig({
            metaOSContext: {
                locale: metaOsLocale || "en-us",
                hostName: metaOsHostName,
                clientType: metaOsHostClientType,
                ring: metaOsHostRingId,
            },
        });
        
        // Initialize the account source list store for Calendar, Places, Bookings and
        // MyOutlook metaos apps using PUID
        const hostedApp = config.app;
        if ((hostedApp === "Calendar" ||
            hostedApp === "Places" ||
            hostedApp === "CalendarDeepLink" ||
            hostedApp === "Bookings" ||
            hostedApp === "MyOutlook") &&
            getAuthTokenEnabled) {
            const decodedToken = await lazyGetOwaDecodedAuthTokenFromMetaOsHubProxy.importAndExecute();
            // Cleanup once MetaOS begins to transmit PUID https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/253607
            if (decodedToken?.puid) {
                await markFunction(lazyPreInitOwaAccountSourceListStoreProxy.importAndExecute, "MetaOSInitAccountStore")(decodedToken.puid, metaOsUser?.tenant?.id, metaOsUser?.loginHint, metaOsUser?.userPrincipalName);
                updateServiceConfig(await lazyGetAccountServiceConfigProxy.importAndExecute());
            }
        }
        else if (hostedApp === "CalendarDeepLink" && isHostedCalendar()) {
            // Match the hosted Calendar window's NAA-path pre-init so that both windows
            // derive the same persistenceId (coprincipal source id) for the user.
            const didPreInit = await lazyPreInitAccountStoreForNestedAppAuthProxy.importAndExecute(metaOsUser);
            if (didPreInit) {
                updateServiceConfig(await lazyGetAccountServiceConfigProxy.importAndExecute());
            }
        }
        
        return oldRunBeforeStart?.(startConfig);
    });
};
